/* eslint-disable react-hooks/exhaustive-deps */
import { useMediaQuery, Grid, Box, useTheme, Typography, Button, Container, Skeleton, Link } from "@mui/material";
import { useNavigate } from "react-router-dom";
import CustomRightImage from "@components/CustomRightImagev2";
import { useLazyGetTaxSystemConfigByRfcQuery } from "@api/applications";
import { useEffect, useState } from "react";
import { TaxSystemConfigResponse } from "@interfaces/index";
import { amountToText, calculateAmountMonthly, formatCurrency } from "@helpers/funciones";

import { StyledSlider } from "@components/CustomSlider";

import useInterfaceConfig from "@hooks/useInterfaceConfig";

const CotizadorFinbe = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const interfaceConfig = useInterfaceConfig();

  const [getTaxSystemConfigByRfc] = useLazyGetTaxSystemConfigByRfcQuery();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [amountMarks, setAmountMarks] = useState<{ value: number; label: string }[]>([]);
  const [termMarks, setTermMarks] = useState<{ value: number; label: string }[]>([]);

  const [cotizadorConfig, setCotizadorConfig] = useState<TaxSystemConfigResponse>({
    id: "",
    code: "",
    product_id: "",
    minAmount: 0,
    maxAmount: 0,
    stepAmount: 0,
    minTerm: 0,
    maxTerm: 0,
    stepTerm: 0,
    defaultAmount: 0,
    defaultTerm: 0,
    tax_system_type: "",
  });

  const [selectedAmount, setSelectedAmount] = useState<number>(cotizadorConfig.defaultAmount);
  const [selectedTerm, setSelectedTerm] = useState<number>(cotizadorConfig.defaultTerm);
  const [monthlyRate, setMonthlyRate] = useState<number>(0);
  const [preauthorizedAmount, setPreauthorizedAmount] = useState<number>(0);
  const [companyName, setCompanyName] = useState<string>("");
  const [rfcCompany, setRfcCompany] = useState<string>("");

  useEffect(() => {
    if (!rfcCompany) return;

    const fetchData = async () => {
      try {
        setIsLoading(true);

        const response = await getTaxSystemConfigByRfc(rfcCompany).unwrap();
        if (response && response.length > 0) {
          const config = response[0];
          const amountPreauthorized = config.preauthorized_amount;
          setCotizadorConfig({
            id: config.tax_system_id,
            code: config.tax_system.code,
            product_id: config.tax_system.product_id,
            minAmount: config.tax_system.min_amount,
            maxAmount: amountPreauthorized,
            stepAmount: config.tax_system.jump_amount,
            minTerm: config.tax_system.min_term,
            maxTerm: config.tax_system.max_term,
            stepTerm: config.tax_system.jump_term,
            defaultAmount: amountPreauthorized,
            defaultTerm: config.tax_system.default_term,
            tax_system_type: config.tax_system.tax_system_type,
          });
          setPreauthorizedAmount(amountPreauthorized);
          setCompanyName(config.name);
        }
      } catch (error) {
        console.error("Error fetching tax system configs", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [rfcCompany]);

  useEffect(() => {
    const { minAmount, maxAmount, stepAmount, minTerm, maxTerm, stepTerm, defaultAmount, defaultTerm } =
      cotizadorConfig;

    if (minAmount < maxAmount && stepAmount > 0) {
      const marks = [];

      marks.push({ value: minAmount, label: amountToText(minAmount) });
      marks.push({ value: maxAmount, label: amountToText(maxAmount) });

      setAmountMarks(marks);
    }

    if (minTerm < maxTerm && stepTerm > 0) {
      const marks = [];

      for (let i = minTerm; i <= maxTerm; i += 3) {
        marks.push({ value: i, label: `${i}` });
      }

      setTermMarks(marks);
      setSelectedAmount(defaultAmount);
      setSelectedTerm(defaultTerm);
    }
  }, [cotizadorConfig]);

  useEffect(() => {
    if (selectedAmount && selectedTerm) {
      const monthly = calculateAmountMonthly(selectedAmount, selectedTerm, interfaceConfig?.interest_rate);
      setMonthlyRate(monthly);
    }
  }, [selectedAmount, selectedTerm]);

  const setConditions = () => {
    if (!cotizadorConfig.product_id || !selectedAmount || !selectedAmount || !monthlyRate) {
      return;
    }
    const sourceCode = sessionStorage.getItem("source_code");

    let urlPortal = `/producto/creditosimple/crear_cuenta?tax_system_id=${cotizadorConfig.id}&product_id=${cotizadorConfig.product_id}&person_type=${cotizadorConfig.code}&loan_amount=${selectedAmount}&loan_term=${selectedTerm}&tax_system_type=${cotizadorConfig.tax_system_type}&rfcCompany=${rfcCompany}`;
    if (sourceCode) {
      urlPortal += `&source_code=${sourceCode}`;
    }
    navigate(urlPortal);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sourceCode = params.get("code");
    const rfc = params.get("rfc");

    if (!rfc){
      navigate("/");
      return;
    }
    
    setRfcCompany(rfc);
    if (sourceCode) {
      sessionStorage.setItem("source_code", sourceCode);
    } else {
      sessionStorage.removeItem("source_code");
    }
  }, []);

  return (
    <>
      <Grid container height="100%">
        <Grid item xs={12} md={8}>
          <Container maxWidth={"sm"}>
            <Typography
              mt={4}
              mb={2}
              fontWeight={600}
              fontSize={"20px"}
              color={theme?.palette?.primary?.main}
              textAlign={"center"}
            >
              ¡Felicidades! <br />
              {companyName}
            </Typography>

            <Typography
              fontWeight={300}
              fontSize={"16px"}
              color={theme?.palette?.primary?.dark}
              textAlign={"center"}
              mt={2}
              mb={4}
            >
              Tienes un crédito preautorizado de hasta
            </Typography>

            <Typography
              mb={2}
              mt={2}
              textAlign={"center"}
              variant="h4"
              fontWeight={theme?.palette?.fontWeights?.semibold}
              color={theme?.palette?.primary?.main}
            >
              {isNaN(preauthorizedAmount) || !isFinite(preauthorizedAmount) ? "0" : formatCurrency(preauthorizedAmount)}{" "}
              <Typography
                variant="h5"
                component={"span"}
                fontWeight={theme?.palette?.fontWeights?.semibold}
                color={theme?.palette?.primary?.main}
              >
                MXN
              </Typography>
            </Typography>

            <Box>
              <Box mt={1} ml={isMobileOrTablet ? 3 : 1} mr={isMobileOrTablet ? 3 : 1}>
                <Typography
                  fontWeight={500}
                  fontSize={"16px"}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  Selecciona el monto
                </Typography>

                <Typography
                  fontWeight={700}
                  fontSize={"13px"}
                  mt={1}
                  color={theme?.palette?.primary?.main}
                  textAlign={"center"}
                >
                  {amountToText(selectedAmount)}
                </Typography>

                {!isLoading ? (
                  <StyledSlider
                    aria-label="amount"
                    min={cotizadorConfig.minAmount}
                    max={cotizadorConfig.maxAmount}
                    step={cotizadorConfig.stepAmount}
                    marks={amountMarks}
                    defaultValue={cotizadorConfig.defaultAmount}
                    value={selectedAmount}
                    onChange={(_event, newValue) => setSelectedAmount(newValue as number)}
                  />
                ) : (
                  <Skeleton variant="rectangular" animation="wave" />
                )}
              </Box>

              <Box mb={1} ml={isMobileOrTablet ? 3 : 1} mr={isMobileOrTablet ? 3 : 1}>
                <Typography
                  fontWeight={500}
                  fontSize={"16px"}
                  mb={2}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  Selecciona el plazo en meses
                </Typography>

                {!isLoading ? (
                  <StyledSlider
                    aria-label="term"
                    min={cotizadorConfig.minTerm}
                    max={cotizadorConfig.maxTerm}
                    step={1}
                    defaultValue={cotizadorConfig.defaultTerm}
                    marks={termMarks}
                    value={selectedTerm}
                    onChange={(_event, newValue) => setSelectedTerm(newValue as number)}
                    valueLabelDisplay="on"
                  />
                ) : (
                  <Skeleton variant="rectangular" animation="wave" />
                )}
              </Box>

              <Typography fontWeight={700} fontSize={"16px"} color={theme?.palette?.primary?.main} textAlign={"center"}>
                Pago mensual
              </Typography>

              <Typography
                mb={2}
                textAlign={"center"}
                variant="h4"
                fontWeight={theme?.palette?.fontWeights?.semibold}
                color={theme?.palette?.primary?.dark}
              >
                {isNaN(monthlyRate) || !isFinite(monthlyRate) ? "0" : formatCurrency(monthlyRate)}{" "}
                <Typography
                  variant="h5"
                  component={"span"}
                  fontWeight={theme?.palette?.fontWeights?.semibold}
                  color={theme?.palette?.primary?.dark}
                >
                  MXN
                </Typography>
              </Typography>

              <Typography textAlign={"center"} color={theme?.palette?.primary?.dark}>
                Al continuar estás aceptando los{" "}
                <Link href={interfaceConfig?.terminos_condiciones_empresa || "#"} target="_blank">
                  <Typography component={"span"}>términos y condiciones de la promoción</Typography>
                </Link>
                .
              </Typography>

              <Box display="flex" justifyContent="center" mb={2} mt={2}>
                <Button variant="contained" onClick={setConditions} disabled={isLoading}>
                  Continuar
                </Button>
              </Box>
            </Box>
          </Container>
        </Grid>
        {!isMobileOrTablet && (
          <Grid item xs={12} md={4}>
            <CustomRightImage />
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default CotizadorFinbe;
